import React, { useState, useMemo } from "react";
import "bootstrap/dist/css/bootstrap.min.css";

export default function IncumbenciasList({ incumbencias, selected, onSelect }) {
  const [buscador, setBuscador] = useState("");

  const toggleMateria = (nombreMateria) => {
    const newSelected = selected.includes(nombreMateria)
      ? selected.filter((m) => m !== nombreMateria)
      : [...selected, nombreMateria];
    onSelect(newSelected);
  };

  // Normalizar sin acentos
  const normalizar = (str) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const filtradas = useMemo(() => {
    if (!buscador.trim()) return incumbencias;
    const buscadorNormalizado = normalizar(buscador);
    return incumbencias.filter((inc) =>
      normalizar(inc.nombreMateria).includes(buscadorNormalizado)
    );
  }, [buscador, incumbencias]);

  // Agrupar por escuela → categoría → materias
  const agrupadas = useMemo(() => {
    const acc = {};
    filtradas.forEach((inc) => {
      const escuelas =
        inc.descripcion?.split(",").map((e) => e.trim()) || ["Sin Escuela"];
      const categoria = inc.categoria?.descripcion || "Sin Categoría";

      escuelas.forEach((escuela) => {
        if (!acc[escuela]) acc[escuela] = {};
        if (!acc[escuela][categoria]) acc[escuela][categoria] = [];

        if (
          !acc[escuela][categoria].some(
            (m) => m.nombreMateria === inc.nombreMateria
          )
        ) {
          acc[escuela][categoria].push(inc);
        }
      });
    });

    // Ordenar alfabeticamente
    Object.keys(acc).forEach((esc) => {
      Object.keys(acc[esc]).forEach((cat) => {
        acc[esc][cat].sort((a, b) =>
          a.nombreMateria.localeCompare(b.nombreMateria, "es", {
            sensitivity: "base",
          })
        );
      });
    });

    return Object.fromEntries(
      Object.entries(acc).sort(([a], [b]) =>
        a.localeCompare(b, "es", { sensitivity: "base" })
      )
    );
  }, [filtradas]);

  if (!incumbencias || incumbencias.length === 0) {
    return (
      <p className="text-muted fst-italic small">
        No se encontraron materias.
      </p>
    );
  }

  return (
    <div className="mb-3" >
      {/* Buscador */}
      <input
        type="text"
        placeholder="Buscar por nombre de materia..."
        value={buscador}
        style={{width:"400px"}}
        onChange={(e) => setBuscador(e.target.value)}
        className="form-control mb-3"
      />

      {Object.keys(agrupadas).map((escuela) => (
        <Accordion
          key={escuela}
          escuela={escuela}
          categorias={agrupadas[escuela]}
          selected={selected}
          toggleMateria={toggleMateria}
        />
      ))}
    </div>
  );
}

function Accordion({ escuela, categorias, selected, toggleMateria }) {
  const totalMaterias = Object.values(categorias).reduce(
    (acc, materias) => acc + materias.length,
    0
  );

  const seleccionadasEnEscuela = Object.values(categorias).reduce(
    (acc, materias) =>
      acc + materias.filter((m) => selected.includes(m.nombreMateria)).length,
    0
  );

  const tieneSeleccionadas = seleccionadasEnEscuela > 0;
  const [open, setOpen] = useState(tieneSeleccionadas);

  // Colores dinámicos
  const escuelaColorClass = seleccionadasEnEscuela > 0 ? "text-white" : "text-dark";
  const btnBgClass = seleccionadasEnEscuela > 0 ? "bg-primary" : "btn-light";

  return (
    <div className="card mb-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`btn d-flex justify-content-between align-items-center w-100 text-start ${btnBgClass}`}
        style={{ borderRadius: 0 }}
      >
        <span>
          <strong className={escuelaColorClass}>
            {escuela.charAt(0).toUpperCase() + escuela.slice(1)}
          </strong>{" "}
          <span className={seleccionadasEnEscuela > 0 ? "text-white-50" : "text-muted"}>
            ({seleccionadasEnEscuela}/{totalMaterias})
          </span>
        </span>
        <span className={`fw-bold fs-5 ${escuelaColorClass}`}>{open ? "−" : "+"}</span>
      </button>

      {open && (
        <div className="card-body bg-white">
          {Object.keys(categorias).map((categoria) => (
            <div key={categoria} className="mb-3">
              <h6 className="text-secondary text-uppercase small fw-bold mb-2">
                {categoria}
              </h6>
              <div className="d-flex flex-wrap gap-2">
                {categorias[categoria].map((inc) => {
                  const isSelected = selected.includes(inc.nombreMateria);
                  return (
                    <label
                      key={inc.id}
                      className={`border rounded-pill d-flex align-items-center gap-2 ${
                        isSelected
                          ? "bg-success text-white border-success"
                          : "bg-light text-dark"
                      }`}
                      style={{
                        minWidth: "140px",
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        borderRadius: "18px",
                        cursor: "pointer",
                        userSelect: "none",
                        lineHeight: 1.1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleMateria(inc.nombreMateria)}
                        style={{ transform: "scale(0.85)" }}
                      />
                      {inc.id} - {inc.nombreMateria}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
